import { useState } from 'react';
import { Typography, Stack, Divider, Link } from '@newulife/common';
import Price from 'components/common/Price';
import Volume from 'components/common/Volume';
import { useRecoilValue } from 'recoil';
import { formState } from 'state';
import { useProducts } from 'contexts/ProductContext';
import { useField } from 'hooks/useContentfulApi';

export default function CartOrderTotals({ type, subscriptions }) {
  if (type === 'subscription') return <Subscription subscriptions={subscriptions} />;

  return <Default />;
}

const Subscription = ({ subscriptions }) => {
  const [data] = useField('banner1Ref');

  const calculateMonthlyTotal = (subscriptions) => {
    return subscriptions.reduce((total, subscription) => {
      const price = subscription?.AutoShipPrice
        ? subscription.AutoShipPrice?.Price || subscription.Price
        : subscription.Price;
      return total + subscription.Quantity * price;
    }, 0);
  };
  const calculateQV = (subscriptions) => {
    return subscriptions.reduce((total, subscription) => {
      return total + subscription.Quantity * subscription.Volume;
    }, 0);
  };
  const calculateCV = (subscriptions) => {
    return subscriptions.reduce((total, subscription) => {
      return total + subscription.Quantity * subscription.Volume2;
    }, 0);
  };

  const monthlyTotal = calculateMonthlyTotal(subscriptions);
  const cv = calculateCV(subscriptions);
  const qv = calculateQV(subscriptions);

  if (!data) return null;

  const { fields = {} } = data;

  const { item18: totalText = '', item17: cvText = '', item28: qvText = '' } = fields;

  return (
    <Stack gap={1.5}>
      <Divider sx={{ opacity: 0.6 }} />
      <Stack
        display="grid"
        gridTemplateColumns={{
          xs: '1fr 105px',
          sm: '1fr 185px',
        }}
        sx={{
          rowGap: 1,
          columnGap: 1.5,
        }}
        textAlign="right"
      >
        {/* {Discount > 0 && (
          <>
            <Typography variant="body3" color="neutralCool.main">
              {discountText}
            </Typography>
            <Typography
              data-testid="promo-discount-line"
              variant="body3"
              color="neutralCool.main"
              sx={{
                fontSize: 18,
              }}
            >
              -<Price value={Discount} />
            </Typography>
          </>
        )} */}
        <Typography variant="subhead1">{totalText}</Typography>
        <Typography variant="subhead1" data-testid="subscription-order-subtotal">
          <Price value={monthlyTotal} />
        </Typography>
      </Stack>
      <Divider sx={{ opacity: 0.6 }} />
      <Stack
        display="grid"
        gridTemplateColumns={{
          xs: '1fr 105px',
          sm: '1fr 185px',
        }}
        sx={{
          rowGap: 1,
          columnGap: 1.5,
        }}
        textAlign="right"
      >
        <Typography variant="body2" color="neutralCool.main">
          {cvText}
        </Typography>
        <Typography
          variant="body2"
          color="neutralCool.main"
          data-testid="subscription-order-cv-total"
        >
          <Volume type="amt" value={cv} />
        </Typography>
        <Typography variant="body2" color="neutralCool.main">
          {qvText}
        </Typography>
        <Typography
          variant="body2"
          color="neutralCool.main"
          data-testid="subscription-order-qv-total"
        >
          <Volume type="amt" value={qv} />
        </Typography>
      </Stack>
    </Stack>
  );
};

const Default = () => {
  const [data] = useField('banner1Ref');
  const [promotionCodeRef] = useField('banner6Ref.fields');
  const { PromoCodeEvaluations, Totals } = useRecoilValue(formState);
  const {
    CreditAmount,
    Discount,
    HandlingFee,
    HandlingTax,
    ProductTax,
    SubTotal,
    ShippingTax,
    ShippingTotal,
    Total,
    TotalTax,
  } = Totals || {};
  const promoCode = PromoCodeEvaluations?.Evaluations?.[0];
  const { getTotalProductCV, getTotalProductQV, getTotalProductPrice } = useProducts();
  const cv = getTotalProductCV();
  const qv = getTotalProductQV();
  // const subtotal = getTotalProductPrice();
  const [showDetails, setShowDetails] = useState(false);

  const {
    item13: subtotalText,
    item14: taxText,
    item15: shippingText,
    item16: totalText,
    item17: cvText,
    item28: qvText,
    item30: discountText,
    item32: showDetailsText,
    item33: hideDetailsText,
    item34: subtotalTaxText,
    item35: shippingTaxText,
    item36: handlingText,
    item37: handlingTaxText,
  } = data.fields;

  return (
    <Stack gap={1.5}>
      <Divider light />
      <Stack
        display="grid"
        gridTemplateColumns={{
          xs: '1fr 105px',
          sm: '1fr 185px',
        }}
        sx={{
          rowGap: 1,
          columnGap: 1.5,
        }}
        textAlign="right"
      >
        <Typography variant="body1">{subtotalText}</Typography>
        <Typography variant="body1" data-testid="order-subtotal">
          <Price value={SubTotal} />
        </Typography>
        <Typography variant="body2" color="neutralCool.main">
          {shippingText}
        </Typography>
        <Typography variant="body2" color="neutralCool.main" data-testid="order-shipping-total">
          <Price value={ShippingTotal} />
        </Typography>
        {HandlingFee !== 0 && (
          <>
            <Typography variant="body2" color="neutralCool.main">
              {handlingText}
            </Typography>
            <Typography variant="body2" color="neutralCool.main" data-testid="order-handling-total">
              <Price value={HandlingFee} />
            </Typography>
          </>
        )}

        <Stack gap={0.5}>
          {!!promoCode?.PromotionCode && (
            <Stack
              key={`code_${promoCode.PromotionCode}`}
              display="inline-grid"
              gridAutoFlow={{
                sm: 'column',
              }}
              justifyContent="flex-end"
              alignItems="baseline"
              gap={0.5}
            >
              <Typography variant="body2" color="neutralCool.main">
                {promotionCodeRef.text7}: {promoCode.PromotionCode}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack gap={0.5}>
          {!!promoCode?.PromotionCode && (
            <Stack
              key={`promo-discount_${promoCode.PromotionCode}`}
              display="inline-grid"
              gridAutoFlow={{
                sm: 'column',
              }}
              justifyContent="flex-end"
              alignItems="baseline"
              gap={0.5}
            >
              <Typography variant="body2" color="neutralCool.main">
                {/* -<Price value={code.DiscountAmount} /> */}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack
          display="inline-grid"
          gridAutoFlow={{
            sm: 'column',
          }}
          justifyContent="flex-end"
          alignItems="baseline"
          gap={1}
        >
          <Typography variant="body2" color="neutralCool.main">
            {taxText}
          </Typography>
          <Link
            type="button"
            underline="hover"
            sx={{
              fontSize: 14,
              cursor: 'pointer',
            }}
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? hideDetailsText : showDetailsText}
          </Link>
        </Stack>
        <Typography
          variant="body2"
          color="neutralCool.main"
          data-testid="order-tax-total"
          textAlign="right"
        >
          <Price value={TotalTax} />
        </Typography>
        {showDetails && (
          <>
            <Typography variant="body2" color="neutralCool.main">
              {subtotalTaxText}
            </Typography>
            <Typography
              variant="body2"
              color="neutralCool.main"
              data-testid="order-tax-details-product-tax"
            >
              <Price value={ProductTax} />
            </Typography>
            <Typography variant="body2" color="neutralCool.main">
              {shippingTaxText}
            </Typography>
            <Typography
              variant="body2"
              color="neutralCool.main"
              data-testid="order-tax-details-shipping-tax"
            >
              <Price value={ShippingTax} />
            </Typography>
            {HandlingTax !== 0 && (
              <>
                <Typography variant="body2" color="neutralCool.main">
                  {handlingTaxText}
                </Typography>
                <Typography
                  variant="body2"
                  color="neutralCool.main"
                  data-testid="order-tax-details-handling-tax"
                >
                  <Price value={HandlingTax} />
                </Typography>
              </>
            )}
          </>
        )}
      </Stack>
      <Divider light />
      <Stack
        display="grid"
        gridTemplateColumns={{
          xs: '1fr 105px',
          sm: '1fr 185px',
        }}
        sx={{
          rowGap: 1,
          columnGap: 1.5,
        }}
        textAlign="right"
      >
        {Discount > 0 && (
          <>
            <Typography variant="body3" color="neutralCool.main">
              {discountText}
            </Typography>
            <Typography
              data-testid="promo-discount-line"
              variant="body3"
              color="neutralCool.main"
              sx={{
                fontSize: 18,
              }}
            >
              -<Price value={Discount} />
            </Typography>
          </>
        )}
        <Typography variant="subhead1">{totalText}</Typography>
        <Typography variant="subhead1" data-testid="order-total">
          <Price value={Total} />
        </Typography>
      </Stack>
      <Divider sx={{ opacity: 0.6 }} />
      <Stack
        display="grid"
        gridTemplateColumns={{
          xs: '1fr 105px',
          sm: '1fr 185px',
        }}
        sx={{
          rowGap: 1,
          columnGap: 1.5,
        }}
        textAlign="right"
      >
        <Typography variant="body2" color="neutralCool.main">
          {cvText}
        </Typography>
        <Typography variant="body2" color="neutralCool.main" data-testid="order-cv-total">
          <Volume type="amt" value={cv} />
        </Typography>
        <Typography variant="body2" color="neutralCool.main">
          {qvText}
        </Typography>
        <Typography variant="body2" color="neutralCool.main" data-testid="order-qv-total">
          <Volume type="amt" value={qv} />
        </Typography>
      </Stack>
    </Stack>
  );
};
